import { useEffect, useMemo, useRef, useState } from "react";
import * as d3 from "d3";
import { motion } from "framer-motion";
import axios from "axios";
import { Info } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Metric definitions — drives the D3 chart, axis format, and tooltip. */
const METRICS = {
  adr: {
    key: "adr_eur",
    label: "ADR",
    sublabel: "€ / notte",
    format: (v) => `€${d3.format(",")(Math.round(v))}`,
    axisFormat: (v) => `€${d3.format("~s")(v).replace("G", "B")}`,
  },
  occupancy: {
    key: "occupancy_pct",
    label: "Occupancy",
    sublabel: "% listings occupati",
    format: (v) => `${d3.format(".1f")(v)}%`,
    axisFormat: (v) => `${d3.format(".0f")(v)}%`,
  },
  revpar: {
    key: "revpar_eur",
    label: "RevPAR",
    sublabel: "€ / notte disponibile",
    format: (v) => `€${d3.format(",")(Math.round(v))}`,
    axisFormat: (v) => `€${d3.format(".0f")(v)}`,
  },
};

const CONFIDENCE_STYLE = {
  high: {
    label: "Alta",
    bg: "bg-[#3B5138]/20",
    border: "border-[#5C8055]/40",
    text: "text-[#8CB080]",
  },
  medium: {
    label: "Media",
    bg: "bg-[#4A3C1F]/20",
    border: "border-[#8A6E33]/40",
    text: "text-[#C48F56]",
  },
  low: {
    label: "Bassa",
    bg: "bg-[#4A2323]/20",
    border: "border-[#8A4747]/40",
    text: "text-[#C08080]",
  },
};

export default function MarketNeighborhoodsD3() {
  const [bundle, setBundle] = useState(null);
  const [metric, setMetric] = useState("adr");
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/market-neighborhoods`)
      .then((r) => setBundle(r.data))
      .catch(() => setBundle({ neighborhoods: [], hotel_benchmarks: [] }));
  }, []);

  if (!bundle) return null;

  const {
    neighborhoods = [],
    hotel_benchmarks = [],
    milano_avg_adr_eur,
    milano_avg_occupancy_pct,
    milano_avg_revpar_eur,
    milano_total_listings,
    airbnb_snapshot_date,
    airbnb_source_url,
    ota_source,
    disclaimer,
    eyebrow,
    title,
    subhead,
  } = bundle;

  const milanoAvg = {
    adr: milano_avg_adr_eur,
    occupancy: milano_avg_occupancy_pct,
    revpar: milano_avg_revpar_eur,
  }[metric];

  if (!neighborhoods.length) return null;

  return (
    <section
      data-testid="market-neighborhoods"
      className="relative border-t border-white/5 px-6 md:px-12 max-w-[1440px] mx-auto py-20 md:py-32"
    >
      {/* Header */}
      <div className="grid lg:grid-cols-12 gap-10 mb-14 md:mb-20">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-white/50">
            <span className="h-px w-8 bg-[#C48F56]" />
            {eyebrow}
          </div>
          <h2 className="mt-5 font-display text-4xl sm:text-5xl md:text-6xl tracking-tightest leading-[0.95] text-balance">
            {renderRich(title)}
          </h2>
        </div>
        <div className="lg:col-span-7 lg:pt-8">
          <p className="text-white/70 text-base md:text-lg leading-relaxed">
            {subhead}
          </p>
        </div>
      </div>

      {/* Hotel benchmark cards (STR-style, city-wide reference) */}
      {hotel_benchmarks.length > 0 && (
        <div className="mb-12 md:mb-16">
          <div className="flex items-center gap-3 mb-5 text-[10px] uppercase tracking-[0.3em] text-white/40 font-mono">
            <span className="h-px w-6 bg-white/20" />
            Benchmark alberghiero city-wide
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {hotel_benchmarks.map((b, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="rounded-2xl border border-white/10 bg-[#0F0F14] p-6 md:p-8"
                data-testid={`hotel-bench-${i}`}
              >
                <div className="flex items-baseline justify-between mb-5">
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">
                    {b.label}
                  </div>
                  <div className="font-mono text-[10px] text-[#C48F56]/80">
                    {b.source}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <BenchStat
                    label="ADR"
                    value={`€${d3.format(",.0f")(b.adr_eur)}`}
                  />
                  <BenchStat
                    label="Occupancy"
                    value={`${d3.format(".1f")(b.occupancy_pct)}%`}
                  />
                  <BenchStat
                    label="RevPAR"
                    value={`€${d3.format(",.0f")(b.revpar_eur)}`}
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Micro-territorial chart */}
      <div className="rounded-2xl border border-white/10 bg-[#0B0B10] overflow-hidden">
        <div className="p-6 md:p-8 border-b border-white/5 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#C48F56] mb-2">
              Segnale short-stay · Inside Airbnb {airbnb_snapshot_date}
            </div>
            <div className="font-display text-2xl md:text-3xl leading-tight">
              12 quartieri di Milano
              <span className="text-white/45 font-normal text-lg md:text-xl">
                {" "}
                · {milano_total_listings.toLocaleString("it-IT")} listings
              </span>
            </div>
          </div>
          <MetricToggle metric={metric} setMetric={setMetric} />
        </div>

        <D3BarChart
          data={neighborhoods}
          metric={metric}
          milanoAvg={milanoAvg}
          onHover={setHovered}
        />

        {/* Legend + reference note */}
        <div className="border-t border-white/5 px-6 md:px-8 py-4 flex flex-wrap items-center gap-x-8 gap-y-3 font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">
          <span className="inline-flex items-center gap-2">
            <span className="w-3 h-3 rounded-sm bg-[#C48F56]" /> Barra quartiere
          </span>
          <span className="inline-flex items-center gap-2">
            <span className="w-3 h-[2px] bg-white/60" /> Milano medio ·{" "}
            <b className="text-white/85">
              {METRICS[metric].format(milanoAvg)}
            </b>
          </span>
          {hovered && (
            <span className="text-[#C48F56] normal-case tracking-normal text-xs">
              → {hovered.name}
            </span>
          )}
        </div>
      </div>

      {/* Disclaimer + sources */}
      <div className="mt-6 flex items-start gap-3 text-[12px] text-white/45 leading-relaxed max-w-3xl">
        <Info size={14} className="mt-0.5 flex-none text-white/30" />
        <div>
          {disclaimer}
          <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/30">
            Fonti · Inside Airbnb (snapshot {airbnb_snapshot_date}) ·{" "}
            {ota_source} · Italian Hotel Monitor
            {airbnb_source_url && (
              <>
                {" · "}
                <a
                  href={airbnb_source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-[#C48F56] transition-colors"
                >
                  insideairbnb.com
                </a>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Sub-components ---------- */

function BenchStat({ label, value }) {
  return (
    <div>
      <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/40">
        {label}
      </div>
      <div className="mt-1.5 font-display text-2xl md:text-3xl text-[#C48F56] leading-none">
        {value}
      </div>
    </div>
  );
}

function MetricToggle({ metric, setMetric }) {
  return (
    <div className="inline-flex rounded-full border border-white/10 p-1 bg-black/40">
      {Object.entries(METRICS).map(([k, def]) => (
        <button
          key={k}
          onClick={() => setMetric(k)}
          data-testid={`metric-toggle-${k}`}
          className={`px-4 py-1.5 text-[10px] md:text-[11px] uppercase tracking-[0.25em] rounded-full transition-colors ${
            metric === k
              ? "bg-[#C48F56] text-black"
              : "text-white/60 hover:text-white"
          }`}
        >
          {def.label}
        </button>
      ))}
    </div>
  );
}

function renderRich(text = "") {
  const parts = String(text).split(/(\*[^*]+\*)/g).filter(Boolean);
  return parts.map((p, i) => {
    if (p.startsWith("*") && p.endsWith("*")) {
      return (
        <em
          key={i}
          className="font-serif italic text-[#C48F56] not-italic"
          style={{ fontStyle: "italic", fontWeight: 400 }}
        >
          {p.slice(1, -1)}
        </em>
      );
    }
    return <span key={i}>{p}</span>;
  });
}

/* ---------- D3 chart ---------- */

function D3BarChart({ data, metric, milanoAvg, onHover }) {
  const wrapRef = useRef(null);
  const svgRef = useRef(null);
  const [width, setWidth] = useState(900);

  // Responsive width
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0].contentRect.width;
      setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Sorted data
  const sorted = useMemo(() => {
    const key = METRICS[metric].key;
    return [...data].sort((a, b) => b[key] - a[key]);
  }, [data, metric]);

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const margin = { top: 16, right: 60, bottom: 32, left: 160 };
    const barH = 34;
    const gap = 8;
    const innerW = Math.max(200, width - margin.left - margin.right);
    const innerH = sorted.length * (barH + gap);
    const totalH = innerH + margin.top + margin.bottom;

    svg.attr("width", width).attr("height", totalH);

    const g = svg
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    const key = METRICS[metric].key;
    const maxV = d3.max(sorted, (d) => d[key]) || 1;
    const x = d3
      .scaleLinear()
      .domain([0, maxV * 1.08])
      .range([0, innerW]);
    const y = d3
      .scaleBand()
      .domain(sorted.map((d) => d.slug))
      .range([0, innerH])
      .padding(0.15);

    // X-axis (bottom)
    const xAxis = d3
      .axisBottom(x)
      .ticks(width < 640 ? 3 : 5)
      .tickFormat(METRICS[metric].axisFormat)
      .tickSize(-innerH);
    g.append("g")
      .attr("transform", `translate(0,${innerH})`)
      .attr("class", "x-axis")
      .call(xAxis)
      .call((sel) => {
        sel.selectAll(".domain").remove();
        sel
          .selectAll(".tick line")
          .attr("stroke", "rgba(255,255,255,0.06)")
          .attr("stroke-dasharray", "2 2");
        sel
          .selectAll(".tick text")
          .attr("fill", "rgba(255,255,255,0.35)")
          .attr("font-family", "JetBrains Mono, monospace")
          .attr("font-size", 10);
      });

    // Neighborhood labels (left)
    g.append("g")
      .selectAll("text.name")
      .data(sorted)
      .join("text")
      .attr("class", "name")
      .attr("x", -14)
      .attr("y", (d) => y(d.slug) + y.bandwidth() / 2)
      .attr("dy", "0.35em")
      .attr("text-anchor", "end")
      .attr("fill", "rgba(255,255,255,0.85)")
      .attr("font-family", "Instrument Sans, sans-serif")
      .attr("font-size", 13)
      .text((d) => d.name);

    // Bars
    const bars = g
      .append("g")
      .selectAll("rect.bar")
      .data(sorted, (d) => d.slug)
      .join("rect")
      .attr("class", "bar")
      .attr("x", 0)
      .attr("y", (d) => y(d.slug))
      .attr("height", y.bandwidth())
      .attr("width", 0)
      .attr("fill", "#C48F56")
      .attr("fill-opacity", 0.85)
      .attr("rx", 2)
      .attr("data-testid", (d) => `bar-${d.slug}`)
      .style("cursor", "pointer");

    bars
      .transition()
      .duration(700)
      .ease(d3.easeCubicOut)
      .delay((_, i) => i * 40)
      .attr("width", (d) => x(d[key]));

    // Values on right of each bar
    g.append("g")
      .selectAll("text.val")
      .data(sorted, (d) => d.slug)
      .join("text")
      .attr("class", "val")
      .attr("x", (d) => x(d[key]) + 10)
      .attr("y", (d) => y(d.slug) + y.bandwidth() / 2)
      .attr("dy", "0.35em")
      .attr("fill", "#C48F56")
      .attr("font-family", "Instrument Sans, sans-serif")
      .attr("font-size", 13)
      .attr("font-weight", 500)
      .attr("opacity", 0)
      .text((d) => METRICS[metric].format(d[key]))
      .transition()
      .duration(500)
      .delay((_, i) => 500 + i * 40)
      .attr("opacity", 1);

    // Milano average reference line
    if (milanoAvg && milanoAvg > 0 && milanoAvg <= maxV * 1.08) {
      const refX = x(milanoAvg);
      g.append("line")
        .attr("x1", refX)
        .attr("x2", refX)
        .attr("y1", -6)
        .attr("y2", innerH + 6)
        .attr("stroke", "rgba(255,255,255,0.75)")
        .attr("stroke-width", 1.2)
        .attr("stroke-dasharray", "4 3");

      g.append("text")
        .attr("x", refX)
        .attr("y", -10)
        .attr("text-anchor", "middle")
        .attr("fill", "rgba(255,255,255,0.7)")
        .attr("font-family", "JetBrains Mono, monospace")
        .attr("font-size", 9)
        .attr("letter-spacing", "0.1em")
        .text(`MILANO · ${METRICS[metric].format(milanoAvg)}`.toUpperCase());
    }

    // Hover interactions
    bars
      .on("mouseenter", function (_, d) {
        d3.select(this).attr("fill-opacity", 1).attr("fill", "#E4B074");
        onHover?.(d);
      })
      .on("mouseleave", function () {
        d3.select(this).attr("fill-opacity", 0.85).attr("fill", "#C48F56");
        onHover?.(null);
      });
  }, [sorted, metric, width, milanoAvg, onHover]);

  return (
    <div ref={wrapRef} className="w-full overflow-x-auto p-4 md:p-6">
      <svg ref={svgRef} className="block" />
      <NeighborhoodTable data={sorted} metric={metric} />
    </div>
  );
}

/* ---------- Detail table (drill-down data) ---------- */

function NeighborhoodTable({ data, metric }) {
  return (
    <div className="mt-6 md:mt-8 border-t border-white/5 pt-6">
      <div className="grid grid-cols-12 gap-2 px-2 pb-3 font-mono text-[9px] uppercase tracking-[0.25em] text-white/35 border-b border-white/5">
        <div className="col-span-4">Quartiere</div>
        <div className="col-span-2 text-right">ADR</div>
        <div className="col-span-2 text-right">Occ.</div>
        <div className="col-span-2 text-right">RevPAR</div>
        <div className="col-span-1 text-right">Listings</div>
        <div className="col-span-1 text-right">Fiducia</div>
      </div>
      {data.map((d) => {
        const conf = CONFIDENCE_STYLE[d.confidence] || CONFIDENCE_STYLE.high;
        const isActive = METRICS[metric].key;
        return (
          <div
            key={d.slug}
            data-testid={`neighborhood-row-${d.slug}`}
            className="grid grid-cols-12 gap-2 px-2 py-3 border-b border-white/[0.03] text-sm hover:bg-white/[0.02] transition-colors items-center"
          >
            <div className="col-span-4">
              <div className="text-white/90">{d.name}</div>
              {d.cluster && (
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/35 font-mono mt-0.5">
                  {d.cluster}
                </div>
              )}
            </div>
            <div
              className={`col-span-2 text-right font-mono ${
                isActive === "adr_eur" ? "text-[#C48F56]" : "text-white/70"
              }`}
            >
              €{d3.format(",")(Math.round(d.adr_eur))}
            </div>
            <div
              className={`col-span-2 text-right font-mono ${
                isActive === "occupancy_pct" ? "text-[#C48F56]" : "text-white/70"
              }`}
            >
              {d3.format(".1f")(d.occupancy_pct)}%
            </div>
            <div
              className={`col-span-2 text-right font-mono ${
                isActive === "revpar_eur" ? "text-[#C48F56]" : "text-white/70"
              }`}
            >
              €{d3.format(",")(Math.round(d.revpar_eur))}
            </div>
            <div className="col-span-1 text-right font-mono text-white/50">
              {d3.format(",")(d.listings)}
            </div>
            <div className="col-span-1 flex justify-end">
              <span
                className={`text-[9px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full border ${conf.bg} ${conf.border} ${conf.text}`}
                title={d.note}
              >
                {conf.label}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
